import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';
import { BetSlipState, Pari } from '../../models/saloon-models/pari.model';
import { Game, GameFormula } from '../../models/game.model';
import { CourseReunion } from '../../models/active-games-resp.model';
import { Race } from '../../models/race.model';
import { GetProviderReqParams, Operator, Provider, ProviderType } from '../../models/provider.model';
import { HttpService } from '../helper/http.service';
import { ActiveGamesRespData, ActiveRace, Country } from '../../models/active-games-resp.model';
import { BetPayOptions } from '../../models/saloon-models/save-bet-options.model';
import { Partant } from '../../models/partant.model';
import { AccountEndpoints, ApiContexts, TurfEndpoints } from '../../configs/api.configs';
import { environment } from 'src/environments/environment';
import { ACTIVE_GAMES_RESP } from 'src/sample-data/active-game-response.data';
import { Meeting } from 'src/models/saloon-models/meeting.model';
import { TopRaceInfo } from '../../models/top-race-info.model';
import { StorageService } from '../storage/storage.service';
import moment from 'moment';
import { NationaleData } from 'src/models/nationale.model';
import { compareRacesByBetEligibility, formatTime, isRaceFinished, resolveRaceDate } from 'src/functions/format-time.function';
import { DEFAULT_RACECOURSE_CODE } from 'src/functions/casaque-url.function';
import { ConfigService } from '../config/config.service';
import { LoggerService } from '../logger/logger.service';
import { STORAGE_KEYS } from 'src/enums/storage-keys.enum';
import { ThemeService } from '../theme/theme.service';
import { SkeletonServiceService } from '../skeleton/skeleton.service';

@Injectable({
  providedIn: 'root',
})
export class PmuService {
  private readonly activeReunionResp = new BehaviorSubject<ActiveGamesRespData | null>(
    null
  );

  private readonly dataToSubmitSubject = new BehaviorSubject<Pari>({
    meeting: {} as Meeting,
    race: {} as Race,
    prize: 0,
    type: '',
    chevalBases: [],
    chevalAssocies: [],
    mise: 0,
    coefficient: 1,
    montant: 0,
    payable: 0,
    time: '',
    game: {} as Game,
    nCombine: 0,
    completeFormula: false,
    spotEnabled: false,
    multi: 0,
    sformula: '',
  });

  private readonly betOptionsSubject = new BehaviorSubject<BetPayOptions> ({
    operators: [],
    countries: [],
    deviceInfo: {},
    providers: []
  });

  private readonly paymentProviderSubject = new BehaviorSubject<Record<ProviderType, Provider[]>> ({
    "CASHIN" : [], "CASHOUT": []
  })

  readonly nationaleGames$ = new BehaviorSubject<NationaleData[]>([]);

  overLayLoader$ = new BehaviorSubject<boolean>(false);
  // overLayOptions$ = new BehaviorSubject<Partial<OverlayLoaderOption>>({});

  data$ = this.dataToSubmitSubject.asObservable();
  betOptions$ = this.betOptionsSubject.asObservable();
  paymentProviders$ = this.paymentProviderSubject.asObservable();
  refreshAppState = new BehaviorSubject<boolean>(false);
  currentDate: string = "";

  // Flag to prevent persistence during restoration
  private isRestoring = false;
  raceListSubject: BehaviorSubject<any>;
  clearSelections: BehaviorSubject<boolean>;
  loadingRaces$ = new BehaviorSubject<boolean>(true);
  chatTimeout!: number;

  private reunionCodes: { text: string; code: string }[] = [];

  constructor(
    private readonly httpService: HttpService,
    private readonly storage: StorageService,
    private readonly configService: ConfigService,
    private readonly logger: LoggerService,
    private readonly themeService: ThemeService,
    private readonly skelonService: SkeletonServiceService
  ) {
    // this.initializePersistedData();
    this.raceListSubject = new BehaviorSubject<any>([]);
    this.clearSelections = new BehaviorSubject<boolean>(false);
    this.dataToSubmitSubject.next(this.currentDataPari());
    this.loadReunionCodes();
  }

  private loadReunionCodes(): void {
    fetch('statics/reunion_code.json')
      .then((res) => res.json())
      .then((res) => {
        this.reunionCodes = res ?? [];
      })
      .catch(() => {
        this.reunionCodes = [];
      });
  }

  /**
   * Resolves the racecourse code used to build casaque/silk image URLs.
   * The backend-supplied code (`str_Code`, carried on `Race.racecourseCode`)
   * always wins; `reunion_code.json` (matched by substring against the
   * meeting name) is only a fallback for races the backend hasn't covered yet.
   */
  getRacecourseCode(meetingName: string, backendCode?: string): string {
    if (backendCode) {
      return backendCode;
    }

    const normalizedName = (meetingName ?? '').trim().toUpperCase();
    const match = this.reunionCodes.find((entry) => normalizedName.includes(entry.text));

    return match?.code ?? DEFAULT_RACECOURSE_CODE;
  }

  getActiveGames(eventData?: string | Date | null, restore: boolean = false) {
    let dateString = '';
    if (eventData) {
      if (typeof eventData === 'string') {
        // If it's already a string in YYYY-MM-DD format, use it as is
        dateString = eventData;
      } else if (eventData instanceof Date) {
        // If it's a Date object, format it to YYYY-MM-DD using moment
        dateString = moment(eventData).format('YYYY-MM-DD');
      }
    } else {
      // If no eventData provided, use current date formatted as YYYY-MM-DD
      dateString = moment().format('YYYY-MM-DD');
    }

    this.currentDate = dateString;

    let params: any = {
      channel: 7,
      versioncode: 3,
      date_jr: dateString,
    };

    const [gw, api] = [
      this.configService.loadRacesFromCache ?  environment.API.cache_turf_gateway : environment.API.turf_gateway,
      this.configService.loadRacesFromCache ? TurfEndpoints.getCachedActiveGames : TurfEndpoints.getActiveGames,
    ];
    this.loadingRaces$.next(true);
    this.activeReunionResp.next(null);
    this.nationaleGames$.next([]);

    // this.activeReunionResp.next(ACTIVE_GAMES_RESP);
    // this.setNationalGAmes();
    // setTimeout(() => {
    //   this.loadingRaces$.next(false);
    // }, 5000);
    // return;
    return this.httpService
      .getNoAuth<ActiveGamesRespData>(gw, api, params)
      .pipe(tap((res) => res.hasOwnProperty('data')))
      .subscribe((res: any) => {
        const resp = {...res.data };
        if ('HP' in resp) {
          this.activeReunionResp.next(resp);
          this.setNationalGAmes();
          if(restore){
            this.restorePariData();
          }else {
            this.resetBetData();
          }
        }
        this.loadingRaces$.next(false);
      }, (err) => {
        this.loadingRaces$.next(false);
      }, () => {
        this.loadingRaces$.next(false);
      });
  }

  getUpComingRaces(activeReunions: CourseReunion[]) {
    const races: TopRaceInfo[] = [];

    activeReunions.forEach(meeting => {
      meeting.races.forEach(race => {
        if (race.status === '0') {
          const topRaceObj: TopRaceInfo = {
            meetingData: meeting,
            raceData: race,
            time: formatTime(resolveRaceDate(race.racedt, race.race_start))
          };

          races.push(topRaceObj);
        }
      });
    });

    this.raceListSubject.next(races.sort(this.compareObjs));
}

compareObjs(a: TopRaceInfo, b: TopRaceInfo) {
  const raceDtA = a.time;
  const raceDtB = b.time;

  return (raceDtB - raceDtA) * -1; // the -1 is to reverse the sorting order
}

  getBetOptionsData(){
    return this.betOptionsSubject.getValue();
  }

  getCurrentData(): Pari {
    return this.dataToSubmitSubject.getValue();
  }

  getACData(): Observable<{ operators: Partial<Operator[]> | null; countries: Country[] | null }> {
    return this.activeReunionResp.pipe(
      map((data) => ({
        operators: (!data || !data.AC.length) ? null : (data.AC as Array<Partial<Operator[]>>)[0].flat(),
        countries: (!data || !data.AC.length) ? null : (data.AC as Array<Country[]>)[2].flat()
      }))
    );
  }


  getOperatorsFromAC(): Observable<Partial<Operator[]> | null> {
    return this.activeReunionResp.pipe(
      map((data) =>
        !data || !data.AC.length
          ? null
          : (data.AC as Array<Partial<Operator[]>>)[0].flat()
      )
    );
  }

  getCountriesFromAC(): Observable<Country[] | null> {
    return this.activeReunionResp.pipe(
      map((data) => (
        (!data || !data.AC.length) ? null :
        (data.AC as Array<Country[]>)[2].flat()
      ))
    );
  }

  getMeetings(): Observable<Meeting[]> {
    return this.activeReunionResp.pipe(
      // filter((response) => response !== null),
      map((response) => {
        if(response === null){
          return [];
        }
        return response.HP.map((r: CourseReunion) => {
          let races = r.races.map((c) => this.mapRaceDate(c, {id: r.nReunion, name: r.libelle, code: r.str_Code}))
          .sort(compareRacesByBetEligibility);
          return {
            id: r.nReunion,
            name: r.libelle,
            date: r.rd,
            races: races,
            number: +r.numero,
            code: r.str_Code
          };
        });
      })
    );
  }

  setNationalGAmes() {
    const allMeetings = this.getMeetings();

    allMeetings.subscribe((meetings) => {
      const nationaleGames: NationaleData[] = [];
      meetings.forEach(meeting => {
        meeting.races
          .filter(race => race.nationalNo && this.configService.nationale_types_offered.includes(race.nationalNo.toString()))
          .forEach(race => {
            const nationaleData: NationaleData = {
              meetingData: meeting,
              raceData: race,
              time: formatTime(race.date),
              number: race.nationalNo ?? 0
            };

            nationaleGames.push(nationaleData);
          });
      });
      nationaleGames.sort((a, b) => {
        const aFinished = isRaceFinished(a.raceData) ? 1 : 0;
        const bFinished = isRaceFinished(b.raceData) ? 1 : 0;
        return aFinished !== bFinished ? aFinished - bFinished : (new Date(a.raceData.date).getTime() - new Date(b.raceData.date).getTime());
      });
      // Emit the filtered nationale games
      this.nationaleGames$.next(nationaleGames);
    });
  }

  getRaces(meeting: {id: number, name: string, code?: string}): Observable<Race[]> {
    return this.activeReunionResp.pipe(
      // filter((response) => response !== null),
      map((response) => {
        if(response === null){
          return [];
        }
        let races: Race[] = [];
        let responseRaces = response.HP.find(
          (r) => r.nReunion === meeting.id
        )?.races;
        if (responseRaces) {
          races = responseRaces.map((r) => this.mapRaceDate(r, meeting))
          .sort(compareRacesByBetEligibility);
        }
        return races;
      })
    );
  }

  private mapRaceDate(race: ActiveRace, meeting: {id: number, name: string, code?: string}): Race {
    let horses: Partant[] = (race.partants).map((p) => ({
      ...p,
    }));
    const regex = /^(?!Non-).*/;
    return {
      id: race.course_number,
      name: race.libelle,
      date: resolveRaceDate(race.racedt, race.race_start),
      raceStartUtc: race.race_start,
      type: race.particularite,
      competitorsCount: race.partants.flatMap(p => !regex.test(p.status ?? '')).length,
      meetingId: meeting.id,
      distance: race.distance,
      horses: horses,
      partantNumber: +race.partantnb,
      numero: +race.numero,
      status: race.status,
      arrivee: race.arrivee ?? "",
      race_status: race.statut_course ?? "",
      meetingName: meeting.name,
      nationalNo: race.int_National_Number,
      games: this.getGameList(race.course_number, meeting.id, race),
      nReunion: race.reunion_num,
      racecourseCode: meeting.code
    };
  }

  getGames(meetingId: number, raceId: number): Observable<Game[]> {
    return this.activeReunionResp.pipe(
      // filter((response) => response !== null),
      map((response) => {
        if(response === null){
          return [];
        }
        const races = response.HP.find((r) => r.nReunion === meetingId)?.races;
        let responseRace = races?.find((r) => r.course_number === raceId);
        let games: Game[] = [];
        if (responseRace) {
          games = this.getGameList(raceId, meetingId, responseRace);
        }
        return games;
      })
    );
  }

  getGameList(raceId: number, meetingId: number, responseRace: ActiveRace){
    let gameLabels = responseRace.libelleJeux ?? {};
    let gamesCompleteFormula = responseRace.formulaControl ?? {};
    let games: Game[] = responseRace.jeu?.split('@').map((gameCipher) => {
      let params = gameCipher.split('-');

      return {
        code: params[0],
        name: gameLabels[params[0]],
        raceId: raceId,
        meetingId: meetingId,
        baseStake: parseInt(params[1]),
        formulas: <GameFormula[]>params[4].trim()?.replace(/ /g, '')?.split(',') ?? [],
        formulaControl:
          gamesCompleteFormula[params[0]]?.b_control_formule ?? false,
        maxCoefficient: parseInt(params[2]),
        baseHorseCount: parseInt(params[3]),
        color: params[5].toLowerCase(),
        hasOrder: parseInt(params[6]) === 1,
        // flexi: parseInt(params[7]),
        flexi: 0,
        spot: {
          enabled: params[8]?.includes('|') ?? false,
          maxHorseCount: parseInt(params[8]?.split('|')[0] ?? '0'),
          formulas:
            <GameFormula[]>params[8]?.split('|')[1]?.trim()?.replace(/ /g, '')?.split(',') ?? [],
        },
      };
    }) ?? [];

    return games;
  }

  updateData(newData: Partial<Pari>, saving:boolean = true) {
    const currentData = this.dataToSubmitSubject.value;
    const updatedData = { ...currentData, ...newData };
    this.dataToSubmitSubject.next(updatedData);
    // Only persist data if not currently restoring (prevents infinite loops)
    // if (!restoring) {
    //   // this.logger.log('Persisting pari data:', updatedData);
    //   this.persistPariData(updatedData);
    // }
    if(saving){
      this.saveBetSlipData({pari: updatedData, selectedDate: this.currentDate});
    }
    this.themeService.hideVoiceFlowChat$.next(this.skelonService.isMobile() && ((updatedData.chevalBases ?? [])?.length > 0 || (updatedData.chevalAssocies ?? [])?.length > 0));
  }

  removeSelectedRace() {
    this.updateData({race: {} as Race});
  }

  updateBetOptions(optionsField: Partial<BetPayOptions>) {
    const currentData = this.betOptionsSubject.getValue();
    this.betOptionsSubject.next({...currentData, ...optionsField});
  }

// Helper function to get list of non partant horses from a race
  getNonPartantHorses(race: Race): Partant[] {
    return race.horses?.filter(horse => horse.status === 'Non-Partant');
  }


  resetBetData(){
    const initialData = {
      meeting: {} as Meeting,
      race: {} as Race,
      prize: 0,
      type: '',
      chevalBases: [],
      chevalAssocies: [],
      mise: 0,
      coefficient: 1,
      montant: 0,
      payable: 0,
      time: '',
      game: {} as Game,
      nCombine: 0,
      completeFormula: false,
      spotEnabled: false,
      multi: 0,
      sformula: '',
    }

    this.updateData(initialData);
  }

   fetchCashInProviders(params: GetProviderReqParams){
    const hostApi = environment.API.sportsGateway, endpoint = ApiContexts.providersService + AccountEndpoints.paymentProviders;

    return this.httpService.getNoAuth( hostApi, endpoint, params)
      .pipe(tap(res => res.hasOwnProperty('providers')))
      .subscribe((data: any) => {
        const currentValue = this.paymentProviderSubject.value;
        const updatedValue = {
          ...currentValue,
          [params.type]: data['providers'],
        };
        this.paymentProviderSubject.next(updatedValue);
      }
    )

  }

  /**
   * Initialize persisted pari data from localStorage
   */
  private initializePersistedData(): void {
    try {
      const persistedData = this.storage.getSessionItem(STORAGE_KEYS.PARI_DATA);

      if (persistedData) {
        // Check if data is not expired (1 hour)
        const currentTime = Date.now();
        const dataAge = currentTime - persistedData.timestamp;
        const maxAge = 60 * 60 * 1000; // 1 hour in milliseconds

        if (dataAge < maxAge) {
          // Restore pari data without triggering persistence
          this.updateDataWithoutPersistence(persistedData.data);
          this.logger.log('Pari data restored from localStorage', persistedData.data);
        } else {
          // Clear expired data
          this.storage.deleteSessionItem(STORAGE_KEYS.PARI_DATA);
          this.logger.log('Expired pari data cleared');
        }
      }
    } catch (error) {
      this.logger.error('Error initializing persisted data:', error);
      // Clear potentially corrupted data
      this.storage.deleteSessionItem(STORAGE_KEYS.PARI_DATA);
    }
  }

  /**
   * Persist pari data to localStorage with timestamp
   */
  private persistPariData(data: Pari): void {
    try {
      const persistedData = {
        data,
        timestamp: Date.now()
      };

      this.storage.saveSessionItem(STORAGE_KEYS.PARI_DATA, persistedData);
    } catch (error) {
      this.logger.error('Error persisting pari data:', error);
    }
  }

  /**
   * Update data without triggering persistence (used during restoration)
   */
  updateDataWithoutPersistence(newData: Partial<Pari>): void {
    const currentData = this.dataToSubmitSubject.getValue();
    const updatedData = { ...currentData, ...newData };
    this.dataToSubmitSubject.next(updatedData);
  }

  /**
   * Clear persisted pari data from localStorage
   */
  clearPersistedData(): void {
    try {
      this.storage.deleteSessionItem(STORAGE_KEYS.PARI_DATA);
      this.logger.log('Persisted pari data cleared');
    } catch (error) {
      this.logger.error('Error clearing persisted data:', error);
    }
  }

  saveBetSlipData(data: BetSlipState){
    let betSlipData = this.storage.getSessionItem(STORAGE_KEYS.BETSLIP_STATE);
    let newData = {...betSlipData, ...data};
    this.storage.saveSessionItem(STORAGE_KEYS.BETSLIP_STATE, newData);
  }

  restorePariData(){
    let betSlipData: BetSlipState = this.storage.getSessionItem(STORAGE_KEYS.BETSLIP_STATE);
    if(betSlipData.pari){
      this.updateData(betSlipData.pari, false);
    }
  }

  currentDataPari(){
    let betSlipData: BetSlipState = this.storage.getSessionItem(STORAGE_KEYS.BETSLIP_STATE);
    if(betSlipData.pari){
      return betSlipData.pari;
    }

    return {
      meeting: {} as Meeting,
      race: {} as Race,
      prize: 0,
      type: '',
      chevalBases: [],
      chevalAssocies: [],
      mise: 0,
      coefficient: 1,
      montant: 0,
      payable: 0,
      time: '',
      game: {} as Game,
      nCombine: 0,
      completeFormula: false,
      spotEnabled: false,
      multi: 0,
      sformula: '',
    }
  }
}
